import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { Box, DialogContent, TextField, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { updateFiscalReportPaymentRequest } from 'redux/reducers/fiscalReports/reducer';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { EReportStatus } from 'redux/reducers/fiscalReports/constants';
import { TEditFiscalReportDialogProps } from './types';
import { validationSchema } from './validationSchema';

export const EditFiscalReportDialog: FC<TEditFiscalReportDialogProps> = ({
	open,
	setClose,
	data,
	fiscalReportListRequestPayload,
}) => {
	const dispatch = useDispatch();
	const [reportDate, setReportDate] = useState<DateTime | null>(DateTime.now());
	const [reportedVatAmount, setReportedVatAmount] = useState<string>('');
	const [comment, setComment] = useState<string>('');

	const reset = () => {
		setReportDate(DateTime.now());
		setReportedVatAmount('');
		setComment('');
	};

	const formik = useFormik({
		initialValues: {
			reportedVatAmount,
			comment,
			reportDate,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: values => {
			if (!data) return;

			dispatch(
				updateFiscalReportPaymentRequest({
					body: {
						row: data,
						delta: {
							report_date: reportDate
								? (reportDate.toUTC().toISO({ suppressMilliseconds: false }) ?? '')
								: '',
							reported_vat_amount: Number(values.reportedVatAmount),
							comment: values.comment,
							status: EReportStatus.SUBMITTED,
						},
					},
					fiscalReportListRequest: fiscalReportListRequestPayload,
				}),
			);
			formik.resetForm();
			reset();
			setClose();
			// setTimeout(() => {
			// 	window.location.reload();
			// }, 2000);
		},
	});

	useEffect(() => {
		if (!open) return;
		if (!data) return;

		setReportDate(data.report_date ? DateTime.fromISO(data.report_date) : DateTime.now());
		setReportedVatAmount(
			data.reported_vat_amount?.toString() || data.total_vat_due?.toString() || '',
		);
		setComment(data.comment || '');
	}, [data, open]);

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Mark as Filed</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent
					sx={{
						p: '0 12px 0px 12px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
							<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
								Client: <b>{data?.client}</b>
							</Typography>
							<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
								Report Period:{' '}
								<b>
									{data?.report_period_name} {data?.year}
								</b>
							</Typography>
							<Typography component="div" color="primary" sx={{ fontSize: '0.9rem', mb: 2 }}>
								Report Type: <b>{data?.report_type.toUpperCase()}</b>
							</Typography>

							<DatePickerComponent
								id="reportDate"
								label="Report Date"
								value={reportDate}
								onChange={setReportDate}
							/>

							<TextField
								id="reportedVatAmount"
								name="reportedVatAmount"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="Reported VAT Amount"
								value={formik.values.reportedVatAmount}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={formik.touched.reportedVatAmount && Boolean(formik.errors.reportedVatAmount)}
								helperText={formik.touched.reportedVatAmount && formik.errors.reportedVatAmount}
							/>

							<TextField
								id="comment"
								name="comment"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="Comment"
								value={formik.values.comment}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								multiline
								rows={3}
								fullWidth
								error={formik.touched.comment && Boolean(formik.errors.comment)}
								helperText={formik.touched.comment && formik.errors.comment}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						autoFocus
						disabled={
							!reportDate ||
							formik.values.reportedVatAmount === '' ||
							formik.values.reportedVatAmount === null
						}
					>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
